import React, { useRef, useState, useCallback, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { SURFACE, BORDER, TEXT, ACCENT } from "../constants/colors";
import { clientService } from "../../../utils/services/clientService";
import { providerService } from "../../../utils/services/providerService";
import { supplierService } from "../../../utils/services/supplierService";
import { wmsService } from "../../../utils/services/wmsProvider";

dayjs.extend(isoWeek);

const PRESETS = [
  { key: "this_week", label: "This Week" },
  { key: "7d", label: "Last 7 Days" },
  { key: "15d", label: "Last 15 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "custom", label: "Custom" },
];

const LIMIT = 15;

// ── Inline searchable dropdown with server-side search + pagination ──────────
const FilterDropdown = ({
  label,
  value,
  onChange,
  fetchFn,
  displayKey,
  placeholder,
  clientId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reset options when clientId context changes
  useEffect(() => {
    setOptions([]);
    setPage(0);
  }, [clientId]);

  const load = useCallback(
    async (searchVal, pageNum) => {
      setLoading(true);
      try {
        const res = await fetchFn(searchVal, pageNum, clientId);
        const items = res?.data || (Array.isArray(res) ? res : []);
        if (pageNum === 0) setOptions(items);
        else setOptions((prev) => [...prev, ...items]);
        setHasMore(items.length >= LIMIT);
        setPage(pageNum);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [fetchFn, clientId],
  );

  // Debounced fetch on search/open change
  useEffect(() => {
    if (!open) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => load(search, 0), 300);
    return () => clearTimeout(timerRef.current);
  }, [search, open, load]);

  const handleOpen = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 6, left: rect.left });
    }
    setOpen(true);
    setSearch("");
    setPage(0);
    setOptions([]);
  };

  const isActive = !!value;

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        onClick={() => (open ? setOpen(false) : handleOpen())}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          padding: "4px 10px",
          borderRadius: 5,
          border: `1px solid ${isActive ? `${ACCENT.indigo}60` : open ? `${ACCENT.indigo}40` : BORDER.subtle}`,
          background: isActive
            ? `${ACCENT.indigo}18`
            : open
              ? `${ACCENT.indigo}0a`
              : SURFACE.navBg,
          color: isActive ? ACCENT.indigoLight : TEXT.secondary,
          cursor: "pointer",
          fontSize: 11,
          fontWeight: isActive ? 700 : 500,
          fontFamily: "Poppins",
          transition: "all 0.15s",
          minWidth: 100,
          maxWidth: 160,
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {value ? value[displayKey] : label}
        </span>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            flexShrink: 0,
          }}
        >
          {isActive && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              style={{
                fontSize: 10,
                opacity: 0.7,
                cursor: "pointer",
                lineHeight: 1,
              }}
              title="Clear"
            >
              ✕
            </span>
          )}
          <svg
            width="9"
            height="9"
            viewBox="0 0 10 6"
            fill="none"
            style={{
              transition: "transform 0.15s",
              transform: open ? "rotate(180deg)" : "none",
              opacity: 0.6,
            }}
          >
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open && (
        <div
          style={{
            position: "fixed",
            top: panelPos.top,
            left: panelPos.left,
            zIndex: 9999,
            background: "var(--bg-paper, #1a1f2e)",
            border: `1px solid ${BORDER.subtle}`,
            borderRadius: 8,
            boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
            minWidth: 220,
            maxWidth: 280,
            overflow: "hidden",
          }}
        >
          {/* Search input */}
          <div
            style={{
              padding: "8px 10px",
              borderBottom: `1px solid ${BORDER.subtle}`,
            }}
          >
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder || `Search ${label.toLowerCase()}...`}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "5px 8px",
                background: "transparent",
                border: `1px solid ${BORDER.subtle}`,
                borderRadius: 4,
                color: TEXT.secondary,
                fontSize: 11,
                fontFamily: "Poppins",
                outline: "none",
              }}
            />
          </div>

          {/* Options list */}
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {loading && options.length === 0 && (
              <div
                style={{
                  padding: "12px 14px",
                  fontSize: 11,
                  color: TEXT.dim,
                  textAlign: "center",
                }}
              >
                Loading...
              </div>
            )}
            {!loading && options.length === 0 && (
              <div
                style={{
                  padding: "12px 14px",
                  fontSize: 11,
                  color: TEXT.dim,
                  textAlign: "center",
                }}
              >
                No results
              </div>
            )}
            {options.map((opt) => {
              const isSelected = value?._id === opt._id;
              return (
                <button
                  key={opt._id}
                  onClick={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "8px 14px",
                    border: "none",
                    background: isSelected
                      ? `${ACCENT.indigo}20`
                      : "transparent",
                    color: isSelected ? ACCENT.indigoLight : TEXT.secondary,
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: isSelected ? 700 : 400,
                    fontFamily: "Poppins",
                    textAlign: "left",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = `${BORDER.subtle}60`;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {opt[displayKey] || opt._id}
                  {isSelected && <span style={{ fontSize: 9 }}>✓</span>}
                </button>
              );
            })}
            {hasMore && (
              <button
                onClick={() => load(search, page + 1)}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "7px 14px",
                  border: "none",
                  borderTop: `1px solid ${BORDER.subtle}`,
                  background: "transparent",
                  color: TEXT.dim,
                  fontSize: 10,
                  cursor: "pointer",
                  fontFamily: "Poppins",
                  opacity: loading ? 0.5 : 1,
                }}
              >
                {loading ? "Loading..." : "Load more"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FilterBar = ({
  isAdmin,
  selectedClient,
  setSelectedClient,
  selectedChannel,
  setSelectedChannel,
  selectedSupplier,
  setSelectedSupplier,
  selectedWms,
  setSelectedWms,
  dateRange,
  setDateRange,
}) => {
  const dropdownRef = useRef(null);
  const dateButtonRef = useRef(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [datePanelPos, setDatePanelPos] = useState({ top: 0, right: 0 });
  const [showCalendar, setShowCalendar] = useState(false);
  const [customStart, setCustomStart] = useState(null);
  const [pickingEnd, setPickingEnd] = useState(false);

  // clientId context: if admin selected a client, use that; else use logged-in client
  const clientId = selectedClient?._id || localStorage.getItem("client_id");

  // Close date dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setShowCalendar(false);
        setCustomStart(null);
        setPickingEnd(false);
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  const getActiveLabel = () => {
    if (dateRange?.preset === "custom" && dateRange?.startDate) {
      return `${dayjs(dateRange.startDate).format("MMM D")} – ${dayjs(dateRange.endDate).format("MMM D")}`;
    }
    return (
      PRESETS.find((p) => p.key === dateRange?.preset)?.label || "Last 30 Days"
    );
  };

  // Stable fetch functions
  const fetchClients = useCallback(
    (search, page) =>
      clientService.fetch(LIMIT, page * LIMIT, search, undefined, undefined, [
        "_id",
        "business_name",
      ]),
    [],
  );

  const fetchChannels = useCallback(
    (search, page) => providerService.fetch(LIMIT, page * LIMIT, search, "OMS"),
    [],
  );

  const fetchSuppliers = useCallback(
    (search, page, cId) =>
      supplierService.fetch(
        search,
        cId,
        `$limit=${LIMIT}&$skip=${page * LIMIT}`,
      ),
    [],
  );

  const fetchWms = useCallback(
    (search, page, cId) => wmsService.fetch(cId, LIMIT, page * LIMIT),
    [],
  );

  return (
    <div
      style={{
        background: SURFACE.metricStripBg,
        borderBottom: `1px solid ${BORDER.metricDivider}`,
        padding: "8px 24px",
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "nowrap",
        overflowX: "auto",
      }}
    >
      <span
        style={{
          fontSize: 10,
          color: TEXT.dim,
          textTransform: "uppercase",
          letterSpacing: 1,
          marginRight: 4,
          flexShrink: 0,
        }}
      >
        Filters:
      </span>

      {/* Admin only: Client selector */}
      {isAdmin && (
        <FilterDropdown
          label="Client"
          value={selectedClient}
          onChange={(c) => {
            setSelectedClient(c);
            // Reset dependent filters when client changes
            setSelectedChannel(null);
            setSelectedSupplier(null);
            setSelectedWms(null);
          }}
          fetchFn={fetchClients}
          displayKey="business_name"
          placeholder="Search clients..."
        />
      )}

      {/* Channel filter (OMS provider, type=oms) */}
      <FilterDropdown
        label="Channel"
        value={selectedChannel}
        onChange={setSelectedChannel}
        fetchFn={fetchChannels}
        displayKey="name"
        placeholder="Search channels..."
        clientId={clientId}
      />

      {/* Supplier filter */}
      <FilterDropdown
        label="Supplier"
        value={selectedSupplier}
        onChange={setSelectedSupplier}
        fetchFn={fetchSuppliers}
        displayKey="name"
        placeholder="Search suppliers..."
        clientId={clientId}
      />

      {/* Warehouse filter */}
      <FilterDropdown
        label="Warehouse"
        value={selectedWms}
        onChange={setSelectedWms}
        fetchFn={fetchWms}
        displayKey="name"
        placeholder="Search warehouses..."
        clientId={clientId}
      />

      {/* Date range — select dropdown */}
      <div
        ref={dropdownRef}
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 6,
          position: "relative",
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 10, color: TEXT.dim }}>Period:</span>

        <button
          ref={dateButtonRef}
          onClick={() => {
            if (!dropdownOpen && dateButtonRef.current) {
              const rect = dateButtonRef.current.getBoundingClientRect();
              setDatePanelPos({
                top: rect.bottom + 6,
                right: window.innerWidth - rect.right,
              });
            }
            setDropdownOpen((x) => !x);
            if (dropdownOpen) {
              setShowCalendar(false);
              setCustomStart(null);
              setPickingEnd(false);
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 5,
            border: `1px solid ${dropdownOpen ? `${ACCENT.indigo}60` : BORDER.subtle}`,
            background: dropdownOpen ? `${ACCENT.indigo}12` : SURFACE.navBg,
            color: dropdownOpen ? ACCENT.indigoLight : TEXT.secondary,
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 600,
            fontFamily: "Poppins",
            transition: "all 0.15s",
            minWidth: 130,
            justifyContent: "space-between",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <svg
              width="11"
              height="11"
              viewBox="0 0 16 16"
              fill="none"
              style={{ opacity: 0.7 }}
            >
              <rect
                x="1"
                y="3"
                width="14"
                height="12"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path d="M1 7h14" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M5 1v4M11 1v4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            {getActiveLabel()}
          </span>
          <svg
            width="9"
            height="9"
            viewBox="0 0 10 6"
            fill="none"
            style={{
              transition: "transform 0.15s",
              transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
              opacity: 0.6,
            }}
          >
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {dropdownOpen && (
          <div
            style={{
              position: "fixed",
              top: datePanelPos.top,
              right: datePanelPos.right,
              zIndex: 9999,
              background: "var(--bg-paper, #1a1f2e)",
              border: `1px solid ${BORDER.subtle}`,
              borderRadius: 8,
              boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
              overflow: "hidden",
              minWidth: 180,
            }}
          >
            {!showCalendar && (
              <div style={{ padding: "4px 0" }}>
                {PRESETS.map((p) => {
                  const isActive = dateRange?.preset === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => {
                        if (p.key === "custom") {
                          setShowCalendar(true);
                          setCustomStart(null);
                          setPickingEnd(false);
                        } else {
                          setDateRange({ preset: p.key });
                          setDropdownOpen(false);
                        }
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        padding: "8px 14px",
                        border: "none",
                        background: isActive
                          ? `${ACCENT.indigo}18`
                          : "transparent",
                        color: isActive ? ACCENT.indigoLight : TEXT.secondary,
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: isActive ? 700 : 500,
                        fontFamily: "Poppins",
                        textAlign: "left",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive)
                          e.currentTarget.style.background = `${BORDER.subtle}60`;
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive)
                          e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <span>{p.label}</span>
                      {isActive && (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M2 6l3 3 5-5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                      {p.key === "custom" && !isActive && (
                        <svg
                          width="8"
                          height="8"
                          viewBox="0 0 10 10"
                          fill="none"
                          style={{ opacity: 0.5 }}
                        >
                          <path
                            d="M3 1l4 4-4 4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {showCalendar && (
              <div style={{ padding: 12 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 10,
                  }}
                >
                  <button
                    onClick={() => {
                      setShowCalendar(false);
                      setCustomStart(null);
                      setPickingEnd(false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "none",
                      background: "transparent",
                      color: TEXT.dim,
                      cursor: "pointer",
                      padding: 0,
                      fontSize: 11,
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      style={{ marginRight: 4 }}
                    >
                      <path
                        d="M8 2L4 6l4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Back
                  </button>
                  <span
                    style={{
                      fontSize: 10,
                      color: TEXT.dim,
                      marginLeft: "auto",
                      fontFamily: "Poppins",
                    }}
                  >
                    {pickingEnd
                      ? `From ${dayjs(customStart).format("MMM D")} — pick end`
                      : "Pick start date"}
                  </span>
                </div>
                <Calendar
                  value={pickingEnd ? customStart : customStart || new Date()}
                  onClickDay={(date) => {
                    if (!pickingEnd) {
                      setCustomStart(date);
                      setPickingEnd(true);
                    } else {
                      const end = date >= customStart ? date : customStart;
                      const start = date >= customStart ? customStart : date;
                      setPickingEnd(false);
                      setShowCalendar(false);
                      setDropdownOpen(false);
                      setCustomStart(null);
                      setDateRange({
                        preset: "custom",
                        startDate: start.toISOString(),
                        endDate: end.toISOString(),
                      });
                    }
                  }}
                  minDate={pickingEnd ? customStart : undefined}
                  className="custom-calendar"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
